import { useUserIpApi } from '~/api/use-user-ip-api.js';
import { DYNAMIC_YIELD_COOKIES } from '~/constants/cookies.js';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { isDynamicYieldEnabled } from '~/lib/dynamic-yield/dynamic-yield-settings';
import { getCookie } from '~/utils/cookies.js';

const BASE_URL = 'https://direct.dy-api.com';
const COLLECT_BASE_URL = 'https://direct-collect.dy-api.com';

const TIMEOUT_MS = 7000;

/**
 * Dynamic Yield API composable.
 * Provides methods for interacting with Dynamic Yield's API.
 *
 * @returns {import('~/types/dynamic-yield').DynamicYieldApiReturn} API methods.
 */
export function useDynamicYieldApi() {
  const { getUserIp } = useUserIpApi();

  /**
   * Checks if Dynamic Yield is properly initialized.
   *
   * @returns {boolean} Whether Dynamic Yield is initialized.
   */
  const isDynamicYieldInitialized = () => {
    if (!isDynamicYieldEnabled()) {
      return false;
    }

    return window.DY && window.DY.recommendationContext && window.DY.apiKey;
  };

  /**
   * Normalizes the selector input into an object with a names array and optional preview.
   *
   * @param {Object|string} selector - The selector object or name.
   * @returns {import('~/types/dynamic-yield').DynamicYieldSelector} Normalized selector object.
   */
  const normalizeSelector = (selector) => {
    let normalized = {};
    if (typeof selector === 'string') {
      normalized.names = [selector];
    } else if (typeof selector === 'object') {
      if (selector.name) {
        normalized = { ...selector, names: [selector.name] };
        delete normalized.name;
      } else if (selector.names) {
        normalized = { ...selector };
      } else {
        normalized = { ...selector, names: [] };
      }
    }
    const dyApiPreviewParam = new URLSearchParams(window.location.search).get('dyApiPreview');
    if (dyApiPreviewParam) {
      normalized.preview = { ids: [dyApiPreviewParam] };
    }
    return normalized;
  };

  /**
   * Helper function to send a POST request to the Dynamic Yield API.
   *
   * @param {string} url - The endpoint URL.
   * @param {Object} requestBody - The request body object.
   * @param {Object} headers - Additional headers.
   * @returns {Promise<import('~/types/dynamic-yield').DynamicYieldResponse>} The JSON response.
   */
  const dyFetch = async (url, requestBody, headers) => {
    // Validate API key before making the request
    const apiKey = window?.DY?.apiKey;
    if (!apiKey) {
      return Promise.resolve({ error: 'Missing API key', success: false });
    }

    const fetchOptions = {
      body: JSON.stringify(requestBody),
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'DY-API-KEY': apiKey,
        ...headers,
      },
      method: 'POST',
    };

    // Create a timeout promise that rejects after TIMEOUT_MS milliseconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`DynamicYield API request timed out after ${TIMEOUT_MS}ms`));
      }, TIMEOUT_MS);
    });

    // Race the fetch against the timeout
    const response = await Promise.race([fetch(url, fetchOptions), timeoutPromise]);

    if (!response.ok) {
      throw new Error(`DynamicYield API error: ${response.status}`);
    }
    const data = await response.json();

    DataDogLogs.info('[Allbirds pixel] - Dynamic Yield API - dyFetch - success', {
      url,
    });

    return data;
  };

  /**
   * Makes a choose API call for Dynamic Yield recommendations.
   *
   * @param {Object|string} selector - The selector object or name.
   * @param {string} [userIp] - The user's IP address (optional).
   * @returns {Promise<import('~/types/dynamic-yield').DynamicYieldResponse>} A promise resolving to the API response.
   */
  const choose = async (selector, userIp) => {
    if (!isDynamicYieldInitialized()) {
      return Promise.reject(new Error('DynamicYield not properly initialized'));
    }

    const recommendationContext = { ...window.DY.recommendationContext };
    recommendationContext.data = recommendationContext.data ?? [];

    const normalizedSelector = normalizeSelector(selector);
    const ipAddress = userIp || (await getUserIp());
    const serverCookie = getCookie(DYNAMIC_YIELD_COOKIES.SERVER);
    const sessionCookie = getCookie(DYNAMIC_YIELD_COOKIES.SESSION);

    const requestBody = {
      context: {
        device: {
          ip: ipAddress || undefined,
          userAgent: window.navigator.userAgent,
        },
        page: {
          ...recommendationContext,
          location: window.location.href,
        },
      },
      options: {
        returnAnalyticsMetadata: true,
      },
      selector: normalizedSelector,
      session: {
        dy: sessionCookie || '',
      },
      user: {
        dyid: serverCookie,
        dyid_server: serverCookie,
      },
    };

    const url = `${BASE_URL}/v2/serve/user/choose`;
    return await dyFetch(url, requestBody);
  };

  /**
   * Reports click events to Dynamic Yield.
   *
   * @param {Array<import('~/types/dynamic-yield').DynamicYieldEngagement>} engagement - Engagement data.
   * @returns {Promise<Object>} A promise resolving to the API response.
   */
  const reportEngagement = async (engagement) => {
    if (!isDynamicYieldInitialized()) {
      return Promise.resolve({ success: false });
    }

    const hasValidEngagements = Array.isArray(engagement) && engagement.length > 0;
    const hasRequiredIds = engagement?.some((item) => Boolean(item.decisionId || item.slotId));

    if (!hasValidEngagements || !hasRequiredIds) {
      DataDogLogs.info(
        '[Allbirds API] - DynamicYield - Skipping engagement: no valid decision/choices to report',
        {
          engagement,
          hasRequiredIds,
          hasValidEngagements,
        },
      );
      return Promise.resolve({ skipped: true, success: false });
    }

    const serverCookie = getCookie(DYNAMIC_YIELD_COOKIES.SERVER);
    const sessionCookie = getCookie(DYNAMIC_YIELD_COOKIES.SESSION);

    const requestBody = {
      engagements: [...engagement],
      session: { dy: sessionCookie || '' },
      user: {
        dyid: serverCookie,
        dyid_server: serverCookie,
      },
    };

    // Validate API key before making the request
    const apiKey = window?.DY?.apiKey;
    if (!apiKey) {
      DataDogLogs.error(
        '[Allbirds API] - DynamicYield - Missing API key for engagement reporting',
        {
          engagement,
        },
      );
      return { error: 'Missing API key', success: false };
    }

    const url = `${COLLECT_BASE_URL}/v2/collect/user/engagement`;

    try {
      await fetch(url, {
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
          'DY-API-KEY': apiKey,
          Origin: window.location.origin,
          Referer: window.location.href,
          'User-Agent': window.navigator.userAgent,
        },
        keepalive: true,
        method: 'POST',
      });

      return { success: true };
    } catch (error) {
      DataDogLogs.warn(
        '[Allbirds API] - DynamicYield - engagement send blocked or failed silently',
        { error },
      );
      return { success: false };
    }
  };

  return {
    choose,
    isDynamicYieldInitialized,
    reportEngagement,
  };
}
